import React, { useEffect, useRef, useCallback } from 'react';
import './AccessibleModal.css';

/**
 * Shared Accessible Modal Wrapper Component
 * Provides keyboard accessibility for any modal content
 * Issue #115: Full keyboard accessibility support
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {Function} onClose - Close callback
 * @param {string} titleId - ID of the title element for aria-labelledby
 * @param {React.ReactNode} children - Modal content
 * @param {Object} containerStyle - Optional inline styles for the container
 * @param {boolean} canClose - Whether the modal can be closed (default: true)
 */
const AccessibleModal = ({
  isOpen,
  onClose,
  titleId,
  children,
  containerStyle = {},
  canClose = true
}) => {
  const modalRef = useRef(null);
  const previousActiveElement = useRef(null);

  // Store previously focused element and focus modal when it opens
  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement;

      // Focus first focusable element in modal
      setTimeout(() => {
        const focusableElements = modalRef.current?.querySelectorAll(
          'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
        );
        if (focusableElements && focusableElements.length > 0) {
          focusableElements[0].focus();
        }
      }, 10);
    } else if (previousActiveElement.current) {
      // Restore focus when modal closes
      previousActiveElement.current.focus();
    }
  }, [isOpen]);

  // Handle keyboard navigation and focus trap
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;

    if (e.key === 'Escape' && canClose) {
      e.preventDefault();
      onClose();
      return;
    }

    // Focus trap - Tab and Shift+Tab
    if (e.key === 'Tab') {
      const focusableElements = modalRef.current?.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
      );

      if (!focusableElements || focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }
  }, [isOpen, canClose, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      onClick={canClose ? onClose : undefined}
      role="presentation"
    >
      <div
        ref={modalRef}
        className="accessible-modal-container"
        onClick={(e) => e.stopPropagation()}
        style={containerStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        {children}
      </div>
    </div>
  );
};

export default AccessibleModal;
